import {
  FINAL_BREATHER_MS,
  MATCH_DURATION_MS,
  PROTOCOL_BREATHER_MS,
  STAGING_MS,
  SUBJECT_STABILITY,
  protocolForElapsed,
} from '../shared/protocols';
import { generateQuestion, resolveQuestionAnswer } from '../shared/questions';
import { mulberry32 } from '../shared/rng';
import type { PlayerPublic, ProtocolId, Question } from '../shared/types';
import type { RoundResultPayload } from '../shared/messages';
import type { ColorId } from '../shared/colors';
import type { AudioEngine } from './audio';
import { GameScreen } from './game-screen';
import { loadSoloRecord, saveSoloRecord, type SoloRecord } from './storage';
import type { LoreRepository } from './lore/repository';

const LOCAL_UID = 'local-subject';
const MATCH_MS = MATCH_DURATION_MS;

export interface SoloSessionOptions {
  container: HTMLElement;
  audio: AudioEngine;
  name: string;
  lore: LoreRepository;
  onExit: () => void;
  onRestart: () => void;
  onOpenArchive: () => void;
}

export class SoloSession {
  private readonly options: SoloSessionOptions;
  private readonly screen: GameScreen;
  private readonly rng = mulberry32((Math.random() * 0xffffffff) >>> 0);
  private startAt = 0;
  private endAt = 0;
  private protocol: ProtocolId = 1;
  private question: Question | null = null;
  private questionDisplayAt = 0;
  private round = 0;
  private score = 0;
  private correct = 0;
  private wrong = 0;
  private stability = SUBJECT_STABILITY;
  private pollution = 0;
  private bestReactionMs: number | null = null;
  private ended = false;
  private timers = new Set<number>();
  private questionTimer: number | null = null;

  constructor(options: SoloSessionOptions) {
    this.options = options;
    this.screen = new GameScreen(options.container, {
      mode: 'solo',
      selfUid: LOCAL_UID,
      selfName: options.name,
      audio: options.audio,
      lore: options.lore,
      onAnswer: (color) => this.handleAnswer(color),
      onExit: options.onExit,
      onSoloRetry: options.onRestart,
      onReturnLobby: options.onExit,
      onOpenArchive: options.onOpenArchive,
    });
    const self: PlayerPublic = {
      uid: LOCAL_UID,
      name: options.name,
      rating: 1000,
      score: 0,
      correct: 0,
      wrong: 0,
      connected: true,
      ready: true,
      seat: 1,
      stability: SUBJECT_STABILITY,
    };
    this.screen.setPlayers([self], LOCAL_UID);
    this.screen.setProtocol(1, false);
    this.screen.setPollution(0);
    this.screen.setStabilities(this.stabilities());
    this.screen.showCountdown(Date.now() + 3000, 3000, LOCAL_UID);
    void options.audio.resume();
    this.schedule(() => this.beginMatch(), 3000);
  }

  private stabilities(): Record<string, number> {
    return { [LOCAL_UID]: this.stability };
  }

  private beginMatch(): void {
    if (this.ended) return;
    this.startAt = Date.now();
    this.endAt = this.startAt + MATCH_MS;
    this.screen.setProtocol(1, false);
    this.screen.setPollution(this.pollution);
    this.screen.setStabilities(this.stabilities());
    this.schedule(() => this.nextRound(), STAGING_MS);
    this.schedule(() => this.end(true), MATCH_MS);
  }

  private nextRound(): void {
    if (this.ended) return;
    const now = Date.now();
    if (now >= this.endAt) {
      this.end(true);
      return;
    }
    const config = protocolForElapsed(now - this.startAt);

    if (config.id !== this.protocol) {
      this.protocol = config.id;
      this.addPollution(config.id === 6 ? 4 : 2);
      this.screen.setProtocol(config.id, true);
      const breather = config.id === 6 ? FINAL_BREATHER_MS : PROTOCOL_BREATHER_MS;
      this.schedule(() => this.nextRound(), breather);
      return;
    }

    if (this.endAt - now < 700) {
      this.end(true);
      return;
    }

    const question = generateQuestion({
      id: `solo-${this.round + 1}`,
      protocol: config,
      rng: this.rng,
      questionIndex: this.round,
    });
    this.round += 1;
    this.question = question;
    this.questionDisplayAt = now + 450;
    const deadline = this.questionDisplayAt + config.timeLimitMs;
    this.screen.showQuestion({
      question,
      displayAtLocal: this.questionDisplayAt,
      deadlineLocal: deadline,
      pollution: this.pollution,
      stabilities: this.stabilities(),
    });
    this.questionTimer = this.schedule(() => this.handleTimeout(), Math.max(0, deadline - Date.now() + 140));
  }

  private handleAnswer(color: ColorId): void {
    if (this.ended || !this.question) return;
    const question = this.question;
    this.question = null;
    this.clearQuestionTimer();
    const reaction = Math.max(0, Date.now() - this.questionDisplayAt);
    const expected = resolveQuestionAnswer(question);

    if (color === expected) {
      this.score += 1;
      this.correct += 1;
      this.bestReactionMs =
        this.bestReactionMs === null ? reaction : Math.min(this.bestReactionMs, reaction);
      const result: RoundResultPayload = {
        qid: question.id,
        correctColor: expected,
        winnerUid: LOCAL_UID,
        reason: 'correct',
        pollution: this.pollution,
        stabilities: this.stabilities(),
        reactions: { [LOCAL_UID]: reaction },
        answeredUids: [LOCAL_UID],
      };
      this.screen.showResult(result);
      this.schedule(() => this.nextRound(), 900);
      return;
    }

    this.wrong += 1;
    this.stability -= 1;
    this.addPollution(7);
    this.screen.setStabilities(this.stabilities());
    const result: RoundResultPayload = {
      qid: question.id,
      correctColor: expected,
      winnerUid: null,
      reason: 'rejected',
      pollution: this.pollution,
      stabilities: this.stabilities(),
      reactions: { [LOCAL_UID]: reaction },
      answeredUids: [LOCAL_UID],
    };
    this.screen.showResult(result);
    if (this.stability <= 0) {
      this.schedule(() => this.end(false), 1500);
    } else {
      this.schedule(() => this.nextRound(), 900);
    }
  }

  private handleTimeout(): void {
    this.questionTimer = null;
    if (this.ended || !this.question) return;
    const question = this.question;
    this.question = null;
    this.stability -= 1;
    this.addPollution(5);
    this.screen.setStabilities(this.stabilities());
    const result: RoundResultPayload = {
      qid: question.id,
      correctColor: resolveQuestionAnswer(question),
      winnerUid: null,
      reason: 'timeout',
      pollution: this.pollution,
      stabilities: this.stabilities(),
      reactions: { [LOCAL_UID]: null },
      answeredUids: [],
    };
    this.screen.showResult(result);
    if (this.stability <= 0) {
      this.schedule(() => this.end(false), 1500);
    } else {
      this.schedule(() => this.nextRound(), 900);
    }
  }

  private addPollution(amount: number): void {
    this.pollution = Math.max(0, Math.min(100, this.pollution + amount));
    this.screen.setPollution(this.pollution);
  }

  private end(byTime: boolean): void {
    if (this.ended) return;
    this.ended = true;
    this.clearTimers();
    const attempts = this.correct + this.wrong;
    const accuracy = attempts > 0 ? Math.round((this.correct / attempts) * 100) : 0;
    const previous = loadSoloRecord();
    const isRecord = saveSoloRecord({
      score: this.score,
      accuracy,
      bestReactionMs: this.bestReactionMs,
      protocol: this.protocol,
      at: Date.now(),
    });
    const record: SoloRecord | null = loadSoloRecord() ?? previous;
    this.screen.showSoloEnd({
      score: this.score,
      correct: this.correct,
      wrong: this.wrong,
      accuracy,
      bestReactionMs: this.bestReactionMs,
      success: byTime && this.stability > 0,
      protocol: this.protocol,
      record,
      isRecord,
    });
    this.options.audio.setPollution(66);
  }

  destroy(): void {
    this.ended = true;
    this.clearTimers();
    this.screen.destroy();
  }

  private schedule(callback: () => void, delay: number): number {
    if (this.ended) return 0;
    const timer = window.setTimeout(() => {
      this.timers.delete(timer);
      callback();
    }, Math.max(0, delay));
    this.timers.add(timer);
    return timer;
  }

  private clearQuestionTimer(): void {
    if (this.questionTimer !== null) {
      window.clearTimeout(this.questionTimer);
      this.timers.delete(this.questionTimer);
      this.questionTimer = null;
    }
  }

  private clearTimers(): void {
    for (const timer of this.timers) window.clearTimeout(timer);
    this.timers.clear();
    this.questionTimer = null;
  }
}
